import { useState, useCallback } from "react";
import { X, Plus, Loader2, Search, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSkills, Skill } from "@/hooks/useSkills";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface ManageSkillsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ManageSkillsDialog = ({ open, onOpenChange }: ManageSkillsDialogProps) => {
  const { allSkills, teachingSkills, learningSkills, loading } = useSkills();
  const { user } = useAuth();
  const [teachingSearchQuery, setTeachingSearchQuery] = useState("");
  const [learningSearchQuery, setLearningSearchQuery] = useState("");
  const [customSkillName, setCustomSkillName] = useState("");
  const [addingCustomSkill, setAddingCustomSkill] = useState(false);
  const { toast } = useToast();

  const getFilteredSkills = useCallback((query: string) => {
    return allSkills.filter(skill =>
      skill.name.toLowerCase().includes(query.toLowerCase()) ||
      skill.category.toLowerCase().includes(query.toLowerCase())
    );
  }, [allSkills]);

  const handleAddTeachingSkill = async (skillId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('user_skills')
      .insert({ user_id: user.id, skill_id: skillId });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Skill added!",
        description: "Teaching skill added to your profile",
      });
    }
  };

  const handleAddLearningSkill = async (skillId: string) => {
    if (!user) return;

    const { error } = await supabase
      .from('user_learning')
      .insert({ user_id: user.id, skill_id: skillId });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Skill added!",
        description: "Learning goal added to your profile",
      });
    }
  };

  const handleRemoveTeachingSkill = async (userSkillId: string) => {
    const { error } = await supabase
      .from('user_skills')
      .delete()
      .eq('id', userSkillId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRemoveLearningSkill = async (userLearningId: string) => {
    const { error } = await supabase
      .from('user_learning')
      .delete()
      .eq('id', userLearningId);

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAddCustomSkill = async (type: 'teaching' | 'learning') => {
    if (!customSkillName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a skill name",
        variant: "destructive",
      });
      return;
    }

    setAddingCustomSkill(true);

    try {
      const { data: newSkill, error: skillError } = await supabase
        .from('skills')
        .insert({
          name: customSkillName.trim(),
          category: 'custom',
          emoji: '✨'
        })
        .select()
        .single();

      if (skillError) throw skillError;

      if (type === 'teaching') {
        await handleAddTeachingSkill(newSkill.id);
      } else {
        await handleAddLearningSkill(newSkill.id);
      }

      setCustomSkillName("");
      toast({
        title: "Custom skill added!",
        description: `${customSkillName} has been added to your ${type} skills`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setAddingCustomSkill(false);
    }
  };

  const teachingSkillIds = teachingSkills.map(s => s.skill.id);
  const learningSkillIds = learningSkills.map(s => s.skill.id);

  const filteredTeachingSkills = getFilteredSkills(teachingSearchQuery);
  const filteredLearningSkills = getFilteredSkills(learningSearchQuery);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 pt-4 pb-3 border-b">
          <DialogTitle className="text-lg">Manage Skills</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="teaching" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-2 mx-4 mt-3 w-[calc(100%-2rem)]">
            <TabsTrigger value="teaching" className="text-xs">
              I can teach ({teachingSkills.length})
            </TabsTrigger>
            <TabsTrigger value="learning" className="text-xs">
              I want to learn ({learningSkills.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teaching" className="flex-1 overflow-hidden px-4 pb-4 mt-3">
            <div className="flex flex-col h-full space-y-4">
              {/* Selected Skills */}
              {teachingSkills.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {teachingSkills.map((item) => (
                    <Badge 
                      key={item.id} 
                      variant="default"
                      className="gap-1 pr-1 text-xs"
                    >
                      {item.skill.emoji} {item.skill.name}
                      <button
                        onClick={() => handleRemoveTeachingSkill(item.id)}
                        className="ml-1 hover:bg-background/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Add Custom Skill */}
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Input
                    placeholder="Add custom skill..."
                    value={customSkillName}
                    onChange={(e) => setCustomSkillName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustomSkill('teaching')}
                    className="pr-10 h-9 text-sm"
                  />
                  <Button
                    onClick={() => handleAddCustomSkill('teaching')}
                    disabled={addingCustomSkill || !customSkillName.trim()}
                    size="icon"
                    variant="ghost"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    {addingCustomSkill ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search skills..."
                  value={teachingSearchQuery}
                  onChange={(e) => setTeachingSearchQuery(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              
              {/* Skills Grid */}
              <ScrollArea className="flex-1 -mx-2">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-1 px-2">
                    {filteredTeachingSkills.map((skill) => {
                      const isAdded = teachingSkillIds.includes(skill.id);
                      return (
                        <button
                          key={skill.id}
                          onClick={() => !isAdded && handleAddTeachingSkill(skill.id)}
                          disabled={isAdded}
                          className={`flex items-center justify-between p-2.5 rounded-lg text-left transition-colors ${
                            isAdded 
                              ? 'bg-primary/10 border border-primary/20' 
                              : 'hover:bg-secondary border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-base flex-shrink-0">{skill.emoji}</span>
                            <span className="text-sm font-medium truncate">{skill.name}</span>
                            <span className="text-xs text-muted-foreground bg-secondary px-1.5 py-0.5 rounded flex-shrink-0">
                              {skill.category}
                            </span>
                          </div>
                          {isAdded ? (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          ) : (
                            <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>

          <TabsContent value="learning" className="flex-1 overflow-hidden px-4 pb-4 mt-3">
            <div className="flex flex-col h-full space-y-4">
              {/* Selected Skills */}
              {learningSkills.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {learningSkills.map((item) => (
                    <Badge 
                      key={item.id} 
                      variant="outline"
                      className="gap-1 pr-1 text-xs"
                    >
                      {item.skill.emoji} {item.skill.name}
                      <button
                        onClick={() => handleRemoveLearningSkill(item.id)}
                        className="ml-1 hover:bg-background/20 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Add Custom Skill */}
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Input
                    placeholder="Add custom skill..."
                    value={customSkillName}
                    onChange={(e) => setCustomSkillName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCustomSkill('learning')}
                    className="pr-10 h-9 text-sm"
                  />
                  <Button
                    onClick={() => handleAddCustomSkill('learning')}
                    disabled={addingCustomSkill || !customSkillName.trim()}
                    size="icon"
                    variant="ghost"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    {addingCustomSkill ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search skills..."
                  value={learningSearchQuery}
                  onChange={(e) => setLearningSearchQuery(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              
              {/* Skills Grid */}
              <ScrollArea className="flex-1 -mx-2">
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-1 px-2">
                    {filteredLearningSkills.map((skill) => {
                      const isAdded = learningSkillIds.includes(skill.id);
                      return (
                        <button
                          key={skill.id}
                          onClick={() => !isAdded && handleAddLearningSkill(skill.id)}
                          disabled={isAdded}
                          className={`flex items-center justify-between p-2.5 rounded-lg text-left transition-colors ${
                            isAdded 
                              ? 'bg-primary/10 border border-primary/20' 
                              : 'hover:bg-secondary border border-transparent'
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-base flex-shrink-0">{skill.emoji}</span>
                            <span className="text-sm font-medium truncate">{skill.name}</span>
                            <span className="text-xs text-muted-foreground bg-secondary px-1.5 py-0.5 rounded flex-shrink-0">
                              {skill.category}
                            </span>
                          </div>
                          {isAdded ? (
                            <Check className="h-4 w-4 text-primary flex-shrink-0" />
                          ) : (
                            <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex justify-end px-4 py-3 border-t bg-muted/30">
          <Button onClick={() => onOpenChange(false)} size="sm">
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManageSkillsDialog;
